import React, { FC, HTMLAttributes } from "react";
import clsx from "clsx";
import styles from "./html-text-markdown.module.css";
type IHtmlTextProps = HTMLAttributes<HTMLDivElement> & {
  richText?: boolean;
};
const HtmlText: FC<IHtmlTextProps> = (props) => {
  const { children, className, ...other } = props;
  return (
    <div
      className={clsx(
        "prose w-full max-w-full text-black-900 dark:text-white",
        "dark:prose-code:text-white",
        // All headings: canonical Bricolage Grotesque + tighter leading.
        // mt/mb scales tightened so headings don't push too much vertical
        // space on mobile.
        "prose-headings:font-bricolage-grotesque prose-headings:mb-4 prose-headings:mt-6 prose-headings:break-words prose-headings:!leading-tight dark:prose-headings:text-white lg:prose-headings:mb-5 prose-headings:lg:mt-10",
        // Heading scale — fluid clamp ladder. All in the same Bricolage
        // family inherited above. Replaces the old text-h1/h2/h3/h4/h5/h6
        // breakpoint ladder which jumped at lg + xl.
        "prose-h1 prose-h1:text-[clamp(1.625rem,3vw+0.875rem,3rem)] prose-h1:font-medium",
        "prose-h2 prose-h2:text-[clamp(1.375rem,3vw+0.75rem,3rem)] prose-h2:font-medium",
        "prose-h3 prose-h3:text-[clamp(1.25rem,1.5vw+0.875rem,2rem)] prose-h3:font-medium",
        "prose-h4 prose-h4:text-[clamp(1.125rem,1vw+0.875rem,1.5rem)] prose-h4:font-medium",
        "prose-h5 prose-h5:text-[clamp(1rem,0.75vw+0.875rem,1.25rem)] prose-h5:font-normal",
        "prose-h6 prose-h6:text-[clamp(0.9375rem,0.5vw+0.8rem,1.125rem)] prose-h6:font-normal",
        // Paragraph — canonical body clamp + responsive weight (normal on
        // phones for legibility, light on lg+ for the editorial feel).
        "prose-p:mb-3 prose-p:mt-0 prose-p:break-words prose-p:!text-[clamp(0.875rem,0.5vw+0.75rem,1.125rem)] prose-p:font-light prose-p:!leading-relaxed prose-p:text-black-700 lg:prose-p:font-extralight dark:prose-p:text-black-200 prose-p:md:mb-4 lg:prose-p:mb-5",
        "prose-ul:pl-7",
        "prose-ol:list-decimal prose-ol:pl-5",
        // List items — same clamp + weight as paragraphs so bullet lists
        // read at the same scale as surrounding body copy.
        "prose dark:prose-dark prose-li:list-none prose-li:pl-0 prose-li:!text-[clamp(0.875rem,0.5vw+0.75rem,1.125rem)] prose-li:font-light prose-li:text-black-700 prose-li:marker:font-normal prose-li:marker:text-yellow-400 lg:prose-li:font-extralight",
        "prose-pre:whitespace-pre-wrap prose-pre:rounded-none prose-pre:bg-black-50 prose-pre:p-7 prose-pre:text-base prose-pre:font-normal prose-pre:text-gray-500",
        "prose-blockquote:my-7 prose-blockquote:border-l-[3px] prose-blockquote:border-yellow-400 prose-blockquote:bg-primary-50 prose-blockquote:p-5 prose-blockquote:text-blue-900 prose-blockquote:md:my-12 lg:prose-blockquote:p-7",
        // Tables get visual styling only — bg + padding. Horizontal
        // scroll is handled at the outer article wrapper in
        // BlogDetailHtmlText via [&_figure.table]:overflow-x-auto so the
        // CKEditor <figure class="table"> wrapper is the scroll
        // container, not the <table>. The previous prose-table:block +
        // prose-table:overflow-auto forced display:block on the table,
        // which killed table-layout AND made the figure shrink-wrap to
        // the table's intrinsic width — overflow then had nothing to
        // clip and the figure overflowed the column instead.
        "prose-table:bg-black-50 prose-table:p-4 prose-table:md:p-6 prose-table:lg:p-8",
        "prose-th:text-base prose-th:font-normal prose-th:text-black-900 prose-th:md:text-h6",
        "prose-td:pb-3 prose-td:pt-[10px] prose-td:text-sm prose-td:font-normal prose-td:text-gray-500 prose-td:md:pb-[18px] prose-td:md:pt-3 prose-td:md:text-base",
        "prose-thead:border-b-primary-400 prose-thead:p-8",
        "prose-a:text-link prose-a:underline",
        "prose-strong:inline prose-strong:font-medium dark:prose-strong:text-white",
        "prose-video:my-12 prose-video:h-fit prose-video:max-h-[479px] prose-video:w-full",
        "prose-strong: prose-strong:mt-5 prose-strong:block prose-strong:not-italic prose-strong:text-black-900",
        "prose-hr:my-5 prose-hr:border-black-900 prose-hr:md:my-7 prose-hr:lg:my-12",
        [
          styles.markdownList,
          "dark:prose-li:before:border-white", // for dark mode only
        ],
        styles.markdownTable,
        className,
      )}
      {...other}
    >
      {children}
    </div>
  );
};
export default HtmlText;
